import { HostContext, Product } from '../../embed-script/HostContext';
import { ProductConstants } from '../../constants/ProductConstants';

// The sv/en locale files carry the Spiris (SE) privacy policy. Two hosts must not use it, and
// neither can be told apart by UI language — a user can switch language, but not their product or
// market — so both are routed by host context instead:
//   • SpeedLedger AB — a separate legal entity that shares the sv/en locales; identified by host product.
//   • NL (Visma eAccounting) — a separate market whose customers can switch the UI to en/sv and would
//     then fall through to the SE default; identified by hostContext.country so the Dutch policy holds
//     regardless of language.
// SpeedLedger's product carve-out parallels the one in GuidanceCardContent (matched more leniently
// here — across all products, case-insensitive — since this only picks between trusted URLs).
const SPEEDLEDGER_PRIVACY_POLICY_URL = 'https://www.speedledger.se/privacy/';
const NL_PRIVACY_POLICY_URL = 'https://eaccounting.nl/privacyverklaring';

/**
 * Resolves the privacy-policy URL for the current host. `defaultUrl` is the language-keyed value from
 * i18n (the Spiris SE policy for sv/en); it is overridden for hosts a shared locale can't distinguish —
 * SpeedLedger (by product) and the NL market (by country). `hostContext` is tolerated as undefined
 * (callers pass the store value straight through), falling back to `defaultUrl`.
 */
export function resolvePrivacyPolicyUrl(defaultUrl: string, hostContext: HostContext | undefined): string {
    // hostContext is cast from unvalidated host (postMessage) input, so products may not be an array and
    // an element may not be an object — treat both as unknown and guard before use so a malformed shape
    // can't throw here.
    const products: unknown = hostContext?.products;
    const isSpeedledger =
        Array.isArray(products) &&
        products.some((product) => {
            const name = (product as Product | undefined)?.name;
            return (
                typeof name === 'string' &&
                name.toLowerCase() === ProductConstants.Speedledger.ProductName.toLowerCase()
            );
        });
    if (isSpeedledger) {
        return SPEEDLEDGER_PRIVACY_POLICY_URL;
    }

    if (typeof hostContext?.country === 'string' && hostContext.country.toUpperCase() === 'NL') {
        return NL_PRIVACY_POLICY_URL;
    }

    return defaultUrl;
}
